import * as z from 'zod';
import { financialPeriodPreparationMethodSchema } from '../../../enums.mjs';
import { parentNaicsCodesExtractionValue } from '../../naics-codes/naics-codes.mjs';
import { extractionFieldSchema } from '../extraction-field.mjs';

const statementFrequencyDef = ["annual", "semiAnnual", "quarterly", "monthly"];
const ownerResidenceStatusDef = ["own", "rent"];
const caughtValue = (schema) => schema.nullish().catch(null);
const page = z.number().int().positive().nullish().catch(null);
const applicationField = (value) => extractionFieldSchema(value).extend({ page });
const OwnerResidenceStatusField = applicationField(caughtValue(z.enum(ownerResidenceStatusDef)));
const StatementTypeField = applicationField(caughtValue(financialPeriodPreparationMethodSchema));
const StatementFrequencyField = applicationField(caughtValue(z.enum(statementFrequencyDef)));
const LooseNullableBooleanField = applicationField(caughtValue(z.boolean()));
const LooseNumberField = applicationField(caughtValue(z.union([
    z.number(),
    z
        .string()
        .regex(/^-?\d+(\.\d+)?$/)
        .pipe(z.coerce.number()),
])));
const looseStringValue = z.codec(z.union([z.string(), z.number()]), z.string(), {
    decode: (value) => (typeof value === "number" ? String(value) : value),
    encode: (value) => value,
});
const LooseStringField = applicationField(caughtValue(looseStringValue));
const ParentNaicsCodesField = applicationField(parentNaicsCodesExtractionValue()).catch({
    value: null,
});
const ApplicationExtractionOwner = z.object({
    name: LooseStringField,
    spouse: LooseStringField,
    ownershipPct: LooseNumberField,
    ownOrRent: OwnerResidenceStatusField,
    homeAddress: LooseStringField,
    email: LooseStringField,
    spouseEmail: LooseStringField,
    spouseAddress: LooseStringField.optional(),
    ssn: LooseStringField.optional(),
});
const ApplicationExtractionCompany = z.object({
    legalBusinessName: LooseStringField,
    businessAddress: LooseStringField,
    yearsUnderCurrentManagement: LooseStringField,
    federalTaxId: LooseStringField,
    specialty: LooseStringField,
    naicsCodes: ParentNaicsCodesField,
});
const ApplicationExtractionExperience = z.object({
    causedSuretyLoss: LooseNullableBooleanField,
    failedToCompleteJob: LooseNullableBooleanField,
    lostPaymentSuit: LooseNullableBooleanField,
    defaultedOnContract: LooseNullableBooleanField,
    failedInBusiness: LooseNullableBooleanField,
    grossLossOver10PctLast36Months: LooseNullableBooleanField,
    expectsGrossLossOnCurrentProject: LooseNullableBooleanField,
});
const ApplicationExtractionFinancials = z.object({
    statementType: StatementTypeField,
    statementFrequency: StatementFrequencyField,
    fiscalYearEnd: LooseStringField,
    profitableYtd: LooseNullableBooleanField,
    profitableLastFye: LooseNullableBooleanField,
    largestJobAmount: LooseNumberField,
    largestJobDescription: LooseStringField,
});
const AffiliateRecord = z.object({
    name: LooseStringField,
    federalTaxId: LooseStringField,
    address: LooseStringField,
});
const ApplicationExtractionBondRequest = z.object({
    bidBond: LooseNullableBooleanField,
    finalBond: LooseNullableBooleanField,
    bondAmount: LooseNumberField,
    contractAmount: LooseNumberField,
    projectScopeAndDescription: LooseStringField,
    projectLocation: LooseStringField,
    obligeeName: LooseStringField,
    timeForCompletion: LooseStringField,
    liquidatedOver5k: LooseNullableBooleanField,
    maintenanceWarrantyPeriod: LooseStringField,
    consequentialDamages: LooseNullableBooleanField,
    designBuildResponsibility: LooseNullableBooleanField,
    takeoverExistingContract: LooseNullableBooleanField,
    efficiencyGuarantees: LooseNullableBooleanField,
    environmentalRemediationWork: LooseNullableBooleanField,
    workAtSuperfundSite: LooseNullableBooleanField,
    workForSovereignNationOwner: LooseNullableBooleanField,
    renewableContract: LooseNullableBooleanField,
});
const ApplicationExtractionAuthorization = z.object({
    creditReportAuthorized: LooseNullableBooleanField,
    authorizedSignature: LooseStringField,
    signatureDate: LooseStringField,
});
const ApplicationExtractionBanking = z.object({
    lineOfCreditAmount: LooseNumberField,
});
const ApplicationExtractionOutput = z.object({
    company: ApplicationExtractionCompany,
    owners: z.array(ApplicationExtractionOwner),
    affiliates: z.array(AffiliateRecord),
    experience: ApplicationExtractionExperience,
    financials: ApplicationExtractionFinancials,
    bondRequest: ApplicationExtractionBondRequest,
    banking: ApplicationExtractionBanking,
    authorization: ApplicationExtractionAuthorization,
});

export { ApplicationExtractionOutput, ApplicationExtractionOwner, ownerResidenceStatusDef, statementFrequencyDef };
